import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Save, Settings, IndianRupee, Mail, Wrench, AlertTriangle, Percent } from 'lucide-react';
import toast from 'react-hot-toast';
import { PERMISSIONS, hasPermission } from '../../constants/permissions';
import { useAuth } from '../../context/AuthContext';

export default function AdminSettings({ institutionScope = false }) {
  const { user } = useAuth();
  // Platform Config State
  const [config, setConfig] = useState({
    platformFee: '1.00',
    platformGST: '18',
    supportEmail: 'support@seatifyai.com',
    platformName: 'Seatifyai',
    razorpayKeyId: '',
    maintenanceMode: false,
  });
  const [configLoading, setConfigLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [clusterData, setClusterData] = useState({ name: '', description: '', institutions: [] });
  const [clusterLoading, setClusterLoading] = useState(true);
  const [clusterSaving, setClusterSaving] = useState(false);

  useEffect(() => {
    if (user?.role === 'cluster_admin') {
      fetchClusterConfig();
    } else if (!institutionScope) {
      fetchConfig();
    } else {
      setConfigLoading(false);
    }
  }, [user]);

  const canEditSettings = hasPermission(user, PERMISSIONS.SETTINGS_EDIT);

  const fetchClusterConfig = async () => {
    if (!user?.managedClusterId) {
      setClusterLoading(false);
      return;
    }
    setClusterLoading(true);
    try {
      const res = await axios.get(`/api/admin/clusters/${user.managedClusterId}`);
      setClusterData({
        name: res.data.name || '',
        description: res.data.description || '',
        institutions: res.data.institutions || [],
      });
    } catch (err) {
      toast.error('Failed to load cluster configuration');
    } finally {
      setClusterLoading(false);
    }
  };

  const handleSaveClusterConfig = async (e) => {
    e.preventDefault();
    if (!clusterData.name.trim()) return toast.error('Cluster name is required');
    setClusterSaving(true);
    try {
      await axios.patch(`/api/admin/clusters/${user.managedClusterId}`, {
        name: clusterData.name,
        description: clusterData.description,
      });
      toast.success('Cluster settings saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save cluster settings');
    } finally {
      setClusterSaving(false);
    }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get('/api/settings');
      setConfig({
        platformFee: res.data.platformFee?.toString() || '1.00',
        platformGST: res.data.platformGST?.toString() || '18',
        supportEmail: res.data.supportEmail || 'support@seatifyai.com',
        platformName: res.data.platformName || 'Seatifyai',
        razorpayKeyId: res.data.razorpayKeyId || '',
        maintenanceMode: res.data.maintenanceMode || false,
      });
    } catch (err) {
      toast.error('Failed to load platform configuration');
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (!canEditSettings) return toast.error('You dont have any permission to view this page please contact the adminnistator');
    setSaving(true);
    try {
      await axios.put('/api/settings', {
        platformFee: parseFloat(config.platformFee),
        platformGST: parseFloat(config.platformGST),
        supportEmail: config.supportEmail,
        platformName: config.platformName,
        razorpayKeyId: config.razorpayKeyId,
        maintenanceMode: config.maintenanceMode,
      });
      toast.success('Platform configuration saved successfully!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  // Computed: total fee including GST
  const totalFee = (parseFloat(config.platformFee) * (1 + parseFloat(config.platformGST) / 100)).toFixed(2);

  if (user?.role === 'cluster_admin') {
    return (
      <div>
        <div className="mb-8">
          <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>Cluster Settings</h1>
          <p className="text-slate-500 mt-1">Manage cluster details and view associated institutions.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
          <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-slate-50 p-6 border-b border-slate-200 flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                <Settings size={24} />
              </div>
              <div>
                <h2 className="text-xl font-bold text-slate-800">Cluster Profile</h2>
                <p className="text-sm text-slate-500">Update cluster name and details</p>
              </div>
            </div>

            {clusterLoading ? (
              <div className="p-6 flex items-center justify-center h-40">
                <div className="w-7 h-7 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <form onSubmit={handleSaveClusterConfig} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Cluster Name
                  </label>
                  <input
                    type="text"
                    required
                    value={clusterData.name}
                    onChange={(e) => setClusterData(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={clusterData.description}
                    onChange={(e) => setClusterData(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all resize-none"
                    placeholder="Provide details about the cluster..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={clusterSaving}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {clusterSaving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Save size={18} />}
                  {clusterSaving ? 'Saving...' : 'Save Cluster Profile'}
                </button>
              </form>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col">
            <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
              Institutions ({clusterData.institutions?.length || 0})
            </h3>
            {clusterLoading ? (
              <div className="flex-1 flex items-center justify-center py-10">
                <div className="w-7 h-7 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : clusterData.institutions?.length === 0 ? (
              <p className="text-slate-500 text-sm my-auto text-center py-6">No institutions associated with this cluster.</p>
            ) : (
              <div className="space-y-3 overflow-y-auto max-h-[350px] pr-1">
                {clusterData.institutions.map((institution) => (
                  <div key={institution.id || institution._id} className="p-3 border border-slate-100 rounded-xl hover:bg-slate-50 transition-all flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center font-extrabold text-slate-500 text-sm">
                      {institution.logoPath ? (
                        <img src={institution.logoPath} alt="" className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        institution.displayName?.slice(0, 2).toUpperCase() || 'CL'
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-800 text-sm truncate">{institution.displayName || institution.collegeName}</p>
                      <p className="text-xs text-slate-400 truncate">slug: {institution.slug}</p>
                    </div>
                    <div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${institution.active !== false ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
                        {institution.active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Poppins' }}>System Settings</h1>
        <p className="text-slate-500 mt-1">Manage platform configuration.</p>
      </div>

      <div className="max-w-3xl">
        {/* Platform Configuration — NOW LIVE */}
        {!institutionScope && <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="bg-slate-50 p-6 border-b border-slate-200 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-xl flex items-center justify-center">
              <Settings size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">Platform Configuration</h2>
              <p className="text-sm text-slate-500">Live global application settings</p>
            </div>
          </div>

          {configLoading ? (
            <div className="p-6 flex items-center justify-center h-40">
              <div className="w-7 h-7 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <form onSubmit={handleSaveConfig} className="p-6">
              <div className="space-y-4">

                {/* Platform Fee + GST row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
                      <IndianRupee size={14} /> Platform Fee (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={config.platformFee}
                      onChange={(e) => setConfig(prev => ({ ...prev, platformFee: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
                      <Percent size={14} /> GST (%)
                    </label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={config.platformGST}
                      onChange={(e) => setConfig(prev => ({ ...prev, platformGST: e.target.value }))}
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                    />
                  </div>
                </div>

                {/* Live computed total */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-emerald-700">Total charged per registration</span>
                  <span className="text-lg font-extrabold text-emerald-700">₹{isNaN(totalFee) ? '—' : totalFee}</span>
                </div>

                {/* Support Email */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
                    <Mail size={14} /> Support Email
                  </label>
                  <input
                    type="email"
                    value={config.supportEmail}
                    onChange={(e) => setConfig(prev => ({ ...prev, supportEmail: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  />
                </div>

                {/* Platform Name */}
                <div>
                  <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-1">
                    <Wrench size={14} /> Platform Name
                  </label>
                  <input
                    type="text"
                    value={config.platformName}
                    onChange={(e) => setConfig(prev => ({ ...prev, platformName: e.target.value }))}
                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition-all"
                  />
                </div>

                {/* Maintenance Mode */}
                <div className="flex items-center justify-between p-4 border border-amber-200 bg-amber-50 rounded-xl">
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-500" />
                    <div>
                      <p className="text-sm font-bold text-amber-800">Maintenance Mode</p>
                      <p className="text-xs text-amber-600">Disables new student registrations</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={!canEditSettings}
                    onClick={() => setConfig(prev => ({ ...prev, maintenanceMode: !prev.maintenanceMode }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.maintenanceMode ? 'bg-amber-500' : 'bg-slate-200'}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${config.maintenanceMode ? 'translate-x-6' : 'translate-x-1'}`} />
                  </button>
                </div>

                <button
                  type="submit"
                  disabled={saving || !canEditSettings}
                  className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <Save size={18} />}
                  {saving ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </form>
          )}
        </div>}

      </div>
    </div>
  );
}
